import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Zap, Waves, Users, TrendingUp, CalendarCheck, ArrowRight, Sparkles, MapPin, Check, Loader2, Send } from "lucide-react";
import { ShareButton } from "@/components/ShareButton";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import api from "@/lib/api";

const HERO_ENHANCER = "https://images.unsplash.com/photo-1758600588428-2cca8c96bfba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const RETREAT = "https://images.unsplash.com/photo-1778694276944-66df489166e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const BENEFITS = [
  { icon: Eye, title: "Exposure", body: "Get discovered by members actively seeking well-being. Your own storefront, profile, reviews, and placement across the ecosystem put you in front of the right people." },
  { icon: Zap, title: "Ease", body: "One place for bookings, payments, calendar sync, reminders, waivers, and payouts. Spend your energy on your craft — not admin." },
  { icon: Waves, title: "Flow", body: "Recurring sessions, packages, memberships, and automated follow-ups keep clients returning — so your schedule fills itself." },
  { icon: Users, title: "Networking", body: "Join a community of enhancers and locations. Cross-refer, co-host retreats, and grow together within a living pathway." },
  { icon: TrendingUp, title: "Revenue", body: "Transparent tiered revenue splits, custom packages you own, and member pricing that rewards loyalty. You keep more as you grow." },
  { icon: CalendarCheck, title: "Tools", body: "Deep Google Calendar sync, email & SMS reminders, CRM broadcasts, and a full booking flow — enterprise tooling, done for you." },
];

const ENHANCER_PERKS = [
  "Personal storefront with reviews & ratings",
  "Set your own services, prices & availability",
  "Recurring appointments & custom packages",
  "Automatic reminders and calendar sync",
  "Get matched to members through GAUGE",
];
const LOCATION_PERKS = [
  "List your rooms, retreats & group classes",
  "Approve and host visiting enhancers",
  "Multi-day events with per-day schedules",
  "Booking, payments & waivers handled for you",
  "A branded location page members can find",
];

export default function Partner() {
  const [f, setF] = useState({ business_name: "", contact_name: "", email: "", phone: "", city: "", state: "", website: "", description: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name.trim() || !f.email.trim()) { toast.error("Business name and email are required."); return; }
    setSending(true);
    try {
      await api.post("/partner-applications", f);
      setDone(true);
      toast.success("Application received — we'll be in touch soon!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    } finally { setSending(false); }
  };
  return (
    <div className="bg-[#F9F7F3]">
      <Seo
        title="Partner With Us — Become an Enhancer or Center"
        description="Bring your gifts to the Enhancing Your World ecosystem. Offer services, host retreats, and grow your wellness practice with built-in booking, payments, and community."
        path="/partner"
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#144F75] text-white">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${HERO_ENHANCER})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f3a58] via-[#144F75]/90 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-5 py-20 sm:py-28">
          <p className="eyw-label !text-[#D98E73] mb-3 flex items-center gap-2"><Sparkles size={15} /> For Enhancers & Locations</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">Grow your practice within a living ecosystem.</h1>
          <p className="text-base md:text-lg text-white/85 mt-5 max-w-2xl">
            Enhancing Your World brings you exposure, ease, and flow — the clients, the tools, and the community to do what you love and be found for it. Keep your craft; we'll handle the rest.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/contact" data-testid="partner-apply-enhancer" className="eyw-btn !bg-[#D98E73] !text-[#1A242B] hover:!bg-[#c97d63]">Apply as an Enhancer <ArrowRight size={16} /></Link>
            <a href="#partner-apply" data-testid="partner-list-location" className="eyw-btn-ghost !border-white/50 !text-white hover:!bg-white/10">List your Location</a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-6xl mx-auto px-5 py-16 sm:py-20">
        <div className="max-w-2xl">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1A242B]">Why enhancers & locations choose us</h2>
          <p className="text-[#4A5D6B] mt-3">Everything you need to be seen, book with ease, and keep clients in flow — inside one connected pathway.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {BENEFITS.map((b, i) => (
            <div key={b.title} data-testid={`partner-benefit-${b.title.toLowerCase()}`} className="group rounded-2xl bg-white border border-[#E5E0D8] p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-[#144F75]/10 text-[#144F75] group-hover:bg-[#144F75] group-hover:text-white transition-colors"><b.icon size={22} /></span>
              <h3 className="font-serif text-xl text-[#1A242B] mt-4">{b.title}</h3>
              <p className="text-sm text-[#4A5D6B] leading-relaxed mt-2">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Two paths */}
      <section className="bg-white border-y border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20 grid lg:grid-cols-2 gap-8">
          <div className="rounded-3xl border border-[#E5E0D8] overflow-hidden">
            <img src={HERO_ENHANCER} alt="Enhancer" className="h-48 w-full object-cover" />
            <div className="p-7">
              <p className="eyw-label text-[#144F75] flex items-center gap-2"><Users size={15} /> For Enhancers</p>
              <h3 className="font-serif text-2xl text-[#1A242B] mt-2">Share your gift. Fill your calendar.</h3>
              <ul className="mt-4 space-y-2.5">
                {ENHANCER_PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-[#2A3942]"><Check size={16} className="text-[#5C8A72] mt-0.5 shrink-0" /> {p}</li>
                ))}
              </ul>
              <Link to="/contact" data-testid="partner-enhancer-cta" className="eyw-btn mt-6 !py-2.5 text-sm">Apply to enhance <ArrowRight size={15} /></Link>
            </div>
          </div>
          <div className="rounded-3xl border border-[#E5E0D8] overflow-hidden">
            <img src={RETREAT} alt="Location" className="h-48 w-full object-cover" />
            <div className="p-7">
              <p className="eyw-label text-[#9E5C7F] flex items-center gap-2"><MapPin size={15} /> For Locations</p>
              <h3 className="font-serif text-2xl text-[#1A242B] mt-2">Open your space to the pathway.</h3>
              <ul className="mt-4 space-y-2.5">
                {LOCATION_PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-[#2A3942]"><Check size={16} className="text-[#5C8A72] mt-0.5 shrink-0" /> {p}</li>
                ))}
              </ul>
              <a href="#partner-apply" data-testid="partner-location-cta" className="eyw-btn mt-6 !py-2.5 text-sm !bg-[#9E5C7F] hover:!bg-[#8a4e6f]">List your location <ArrowRight size={15} /></a>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-5 py-16 sm:py-20">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#1A242B] text-center">How it works</h2>
        <div className="grid sm:grid-cols-3 gap-6 mt-10">
          {[
            { n: "01", t: "Apply", d: "Tell us about your practice or space. We review and welcome you into the ecosystem." },
            { n: "02", t: "Set up", d: "Build your storefront, services, availability, and packages — we'll guide you." },
            { n: "03", t: "Grow", d: "Get discovered, booked, and paid. Recurring flow keeps your calendar full." },
          ].map((s) => (
            <div key={s.n} className="text-center">
              <span className="font-serif text-5xl text-[#D98E73]">{s.n}</span>
              <h3 className="font-serif text-xl text-[#1A242B] mt-2">{s.t}</h3>
              <p className="text-sm text-[#4A5D6B] mt-1.5">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section id="partner-apply" className="bg-[#F1EDE6] border-y border-[#E5E0D8]" data-testid="partner-apply-section">
        <div className="max-w-2xl mx-auto px-5 py-16 sm:py-20">
          <div className="text-center mb-8">
            <p className="eyw-label text-[#9E5C7F] flex items-center justify-center gap-2"><MapPin size={15} /> Become a Network Partner</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#1A242B] mt-2">Apply to list your center</h2>
            <p className="text-[#4A5D6B] mt-3">Tell us about your space. Once approved, we publish your branded center page and set you up as a Location Manager.</p>
          </div>
          {done ? (
            <div className="rounded-2xl bg-white border border-[#E5E0D8] p-10 text-center" data-testid="partner-apply-success">
              <span className="grid place-items-center h-14 w-14 rounded-full bg-[#5C8A72]/15 text-[#5C8A72] mx-auto mb-4"><Check size={26} /></span>
              <h3 className="font-serif text-2xl text-[#1A242B]">Application received</h3>
              <p className="text-[#4A5D6B] mt-2">Thank you! Our team will review your center and reach out shortly with next steps.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl bg-white border border-[#E5E0D8] p-6 sm:p-8 space-y-4" data-testid="partner-apply-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1A242B] mb-1 block">Center / business name *</label>
                  <input value={f.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="partner-field-business" className="eyw-input w-full" placeholder="Sunrise Wellness Studio" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A242B] mb-1 block">Your name</label>
                  <input value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="partner-field-contact" className="eyw-input w-full" placeholder="Full name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A242B] mb-1 block">Email *</label>
                  <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="partner-field-email" className="eyw-input w-full" placeholder="you@center.com" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A242B] mb-1 block">Phone</label>
                  <input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="partner-field-phone" className="eyw-input w-full" placeholder="(555) 555-5555" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A242B] mb-1 block">City</label>
                  <input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="partner-field-city" className="eyw-input w-full" placeholder="City" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A242B] mb-1 block">State</label>
                  <input value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="partner-field-state" className="eyw-input w-full" placeholder="State" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">Website</label>
                <input value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="partner-field-website" className="eyw-input w-full" placeholder="https://…" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">Tell us about your center</label>
                <textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={4} data-testid="partner-field-description" className="eyw-input w-full" placeholder="Your space, the services you offer, and why you'd like to join the network." />
              </div>
              <button type="submit" disabled={sending} data-testid="partner-apply-submit" className="eyw-btn w-full justify-center !bg-[#9E5C7F] hover:!bg-[#8a4e6f] disabled:opacity-60">
                {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Submit application
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#144F75] text-white">
        <div className="max-w-4xl mx-auto px-5 py-16 sm:py-20 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl">Ready to be found — and to flow?</h2>
          <p className="text-white/85 mt-3 max-w-xl mx-auto">Join the enhancers and locations building a more connected world. Exposure, ease, networking, and the tools to thrive.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <a href="#partner-apply" data-testid="partner-final-cta" className="eyw-btn !bg-[#D98E73] !text-[#1A242B] hover:!bg-[#c97d63]">Start your application <ArrowRight size={16} /></a>
            <div className="pt-2"><ShareButton path="/partner" testid="partner-share-btn" /></div>
          </div>
        </div>
      </section>
    </div>
  );
}
